import FirstPageIcon from '@mui/icons-material/FirstPage'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import LastPageIcon from '@mui/icons-material/LastPage'
import { Box, IconButton, TablePagination, useMediaQuery, useTheme } from '@mui/material'
import { INITIAL_ROWS_PER_PAGE, rowsPerPageOptions } from '../../pages/Products/components/TableDataPagination'

export interface IPaginator {
  pageNumber: number
  pageSize: number
  totalPages: number
  totalElements: number
  handleChangePage: (e: any, newPage: number) => void
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
}

export const defaultPaginator: IPaginator = {
  pageNumber: 0,
  pageSize: INITIAL_ROWS_PER_PAGE,
  totalPages: 0,
  totalElements: 0,
  handleChangePage: () => {},
  handleChangeRowsPerPage: () => {}
}

interface TablePaginationActionsProps {
  count: number
  page: number
  rowsPerPage: number
  onPageChange: (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => void
}

export const TablePaginationActions = (props: TablePaginationActionsProps & { hasNextPage: boolean }) => {
  const { page, rowsPerPage, count, onPageChange, hasNextPage } = props

  const handleFirstPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, 0)
  }

  const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page - 1)
  }

  const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page + 1)
  }

  const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const lastPage = Math.ceil(count / rowsPerPage) - 1
    onPageChange(event, Math.max(0, lastPage))
  }

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={handleFirstPageButtonClick} disabled={!page || page < 1} aria-label="first page">
        <FirstPageIcon />
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={!page || page < 1} aria-label="previous page">
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton onClick={handleNextButtonClick} disabled={!hasNextPage} aria-label="next page">
        <KeyboardArrowRight />
      </IconButton>
      <IconButton onClick={handleLastPageButtonClick} disabled={!hasNextPage} aria-label="next page">
        <LastPageIcon />
      </IconButton>
    </Box>
  )
}

interface ServerPaginationProps {
  component?: React.ElementType<any>
  label: string
  paginator: IPaginator
}

const ServerPagination = ({ component = 'div', label, paginator }: ServerPaginationProps) => {
  const { pageNumber, pageSize, totalPages, totalElements, handleChangePage, handleChangeRowsPerPage } = paginator
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  return (
    <TablePagination
      width={'auto'}
      component={component}
      rowsPerPageOptions={rowsPerPageOptions}
      count={totalElements}
      rowsPerPage={pageSize || INITIAL_ROWS_PER_PAGE}
      page={pageNumber}
      onPageChange={handleChangePage}
      onRowsPerPageChange={handleChangeRowsPerPage}
      ActionsComponent={(props) => <TablePaginationActions {...props} hasNextPage={pageNumber < totalPages - 1} />}
      labelRowsPerPage={label}
      labelDisplayedRows={({ from, to, count }) => {
        if (!count || count === 0) {
          return '0-0 de 0'
        }
        return `${from}-${to} de ${count}`
      }}
      sx={{
        '& div': {
          display: 'flex'
        },
        '& p': {
          alignSelf: 'end'
        },
        ...(isMobile && { display: 'flex', alignSelf: 'start' })
      }}
    />
  )
}

export default ServerPagination
